if (!customElements.get('bundle-add-to-cart')) {
  customElements.define(
    'bundle-add-to-cart',
    class BundleAddToCart extends HTMLElement {
      constructor() {
        super();
        this.addButton = this.querySelector('.bundle-builder__add-button');
        this.buyNowButton = this.querySelector('.bundle-builder__buy-now-button');
        this.errorWrapper = this.querySelector('.bundle-builder__error-message-wrapper');
        this.errorMessage = this.querySelector('.bundle-builder__error-message');
        this.quantityInput = this.querySelector('.bundle-builder__quantity-input input');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (this.addButton) this.addButton.addEventListener('click', () => this.onAddToCart(false));
        if (this.buyNowButton) this.buyNowButton.addEventListener('click', () => this.onAddToCart(true));

        this.querySelectorAll('.bundle-builder__component-variant-select').forEach((select) => {
          select.addEventListener('change', this.onVariantChange.bind(this));
        });

        this.subscribeToggle = this.querySelector('.bundle-builder__subscribe-toggle-input');
        this.frequencyOptions = this.querySelector('.bundle-builder__frequency-options');
        if (this.subscribeToggle) {
          this.subscribeToggle.addEventListener('change', () => {
            if (this.frequencyOptions) this.frequencyOptions.hidden = !this.subscribeToggle.checked;
          });
        }
        this.querySelectorAll('.bundle-builder__frequency-button').forEach((button) => {
          button.addEventListener('click', () => {
            this.querySelectorAll('.bundle-builder__frequency-button').forEach((b) => b.classList.remove('is-selected'));
            button.classList.add('is-selected');
          });
        });
      }

      onVariantChange(event) {
        const select = event.target;
        const input = this.querySelector(
          `.bundle-builder__component-input[data-component-index="${select.dataset.componentIndex}"]`
        );
        if (!input) return;
        input.dataset.variantId = select.value;
        const option = select.options[select.selectedIndex];
        input.dataset.unavailable = option.disabled ? 'true' : 'false';
      }

      get bundleQuantity() {
        if (!this.quantityInput) return 1;
        return parseInt(this.quantityInput.value, 10) || 1;
      }

      get sellingPlanId() {
        if (this.subscribeToggle) {
          if (!this.subscribeToggle.checked) return null;
          const selected = this.querySelector('.bundle-builder__frequency-button.is-selected');
          return selected ? selected.dataset.sellingPlanId : null;
        }
        const checked = this.querySelector('.bundle-builder__purchase-options input[type="radio"]:checked');
        if (!checked || checked.value === 'one_time') return null;
        return checked.dataset.sellingPlanId || null;
      }

      get items() {
        const bundleQty = this.bundleQuantity;
        const sellingPlanId = this.sellingPlanId;

        return Array.from(this.querySelectorAll('.bundle-builder__component-input')).map((input) => {
          const item = {
            id: input.dataset.variantId,
            quantity: (parseInt(input.dataset.quantity, 10) || 1) * bundleQty,
            unavailable: input.dataset.unavailable === 'true',
            properties: {
              Bundle: this.dataset.bundleName,
              _bundle_handle: this.dataset.bundleHandle,
            },
          };
          if (sellingPlanId) item.selling_plan = sellingPlanId;
          return item;
        });
      }

      onAddToCart(redirectToCheckout) {
        this.setError(false);

        const items = this.items;
        if (items.length === 0) return;

        const unavailable = items.some((item) => item.unavailable);
        if (unavailable) {
          this.setError('One or more items in this bundle are currently unavailable.');
          return;
        }

        const activeButton = redirectToCheckout ? this.buyNowButton : this.addButton;
        [this.addButton, this.buyNowButton].forEach((button) => {
          if (!button) return;
          button.setAttribute('aria-disabled', 'true');
        });
        if (activeButton) {
          activeButton.classList.add('loading');
          const spinner = activeButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        const config = fetchConfig('json');
        config.body = JSON.stringify({
          items: items.map(({ id, quantity, properties, selling_plan }) => ({
            id,
            quantity,
            properties,
            ...(selling_plan ? { selling_plan } : {}),
          })),
          sections: !redirectToCheckout && this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
          sections_url: window.location.pathname,
        });

        if (!redirectToCheckout && this.cart) this.cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message);
              return;
            }

            if (redirectToCheckout) {
              window.location = window.routes.cart_url + '/checkout';
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-add-to-cart',
              cartData: response,
            });

            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Multi-item /cart/add.js responses have no top-level `key` (only
            // single-item responses do), but cart-notification's renderContents
            // needs one to target its item preview. Synthesize it from the
            // last item added so the notification still renders correctly.
            if (!response.key && response.items && response.items.length > 0) {
              response.key = response.items[response.items.length - 1].key;
            }

            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.setError('Something went wrong adding this bundle to your cart. Please try again.');
          })
          .finally(() => {
            [this.addButton, this.buyNowButton].forEach((button) => {
              if (!button) return;
              button.classList.remove('loading');
              button.removeAttribute('aria-disabled');
              const spinner = button.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            });
          });
      }

      setError(message) {
        if (!this.errorWrapper) return;
        this.errorWrapper.toggleAttribute('hidden', !message);
        if (message) this.errorMessage.textContent = message;
      }
    }
  );
}

if (!customElements.get('bundle-media-gallery')) {
  customElements.define(
    'bundle-media-gallery',
    class BundleMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mainImage = this.querySelector('[data-bundle-main-image] img');
        this.querySelectorAll('.bundle-builder__thumbnail').forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => this.onThumbnailClick(thumbnail));
        });
      }

      onThumbnailClick(thumbnail) {
        if (!this.mainImage) return;
        const fullSrc = thumbnail.dataset.fullSrc;
        if (!fullSrc) return;
        this.mainImage.src = fullSrc;
        this.mainImage.removeAttribute('srcset');
        this.querySelectorAll('.bundle-builder__thumbnail').forEach((t) => t.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
      }
    }
  );
}